import { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  getValveStatus,
  openValve,
  closeValve,
} from "../services/deviceService";
import { useTranslation } from "react-i18next";

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

const ValveControl = ({ guid, pollMs = 0, confirmBeforeAction = true }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState("Unknown");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const refreshStatus = useCallback(async () => {
    try {
      setError("");
      const s = await getValveStatus(guid);
      // If API returns Unknown and we already have a known state, keep it
      if (s === "Unknown" && (status === "Open" || status === "Closed")) {
        return;
      }
      setStatus(s || "Unknown");
    } catch (e) {
      setError(e?.message || t("reading_request_error") || "Error");
    }
  }, [guid, t, status]);

  useEffect(() => {
    if (guid) refreshStatus();
  }, [guid, refreshStatus]);

  useEffect(() => {
    if (!pollMs) return;
    const id = setInterval(refreshStatus, pollMs);
    return () => clearInterval(id);
  }, [pollMs, refreshStatus]);

  const runAction = async (action) => {
    if (!guid) return;
    if (confirmBeforeAction) {
      const ok = window.confirm(
        action === "open"
          ? t("confirm_open_valve") || "Open the valve?"
          : t("confirm_close_valve") || "Close the valve?"
      );
      if (!ok) return;
    }

    try {
      setIsLoading(true);
      setError("");
      const expected = action === "open" ? "Open" : "Closed";
      // Optimistic UI update
      setStatus(expected);
      if (action === "open") await openValve(guid);
      else await closeValve(guid);

      // Retry-read status briefly to let device/backend settle
      for (let attempt = 0; attempt < 5; attempt++) {
        const s = await getValveStatus(guid);
        if (s === expected) {
          setStatus(s);
          break;
        }
        if (s === "Unknown") {
          await sleep(500);
          if (attempt === 4) {
            // Keep expected on final attempt if still Unknown
            setStatus(expected);
          }
          continue;
        }
        // Got a concrete but unexpected state (e.g., opposite) — only accept on last attempt
        await sleep(500);
        if (attempt === 4) {
          setStatus(s);
        }
      }
    } catch (e) {
      setError(e?.message || "Action failed");
      try {
        await refreshStatus();
      } catch {
        // No-op: ignore refresh errors
      }
    } finally {
      setIsLoading(false);
    }
  };

  const badgeColor =
    status === "Open"
      ? "bg-emerald-100 text-emerald-700"
      : status === "Closed"
      ? "bg-red-100 text-red-700"
      : "bg-gray-100 text-gray-700";

  const baseDisabled = isLoading || !guid;
  const disableOpen = baseDisabled || status === "Open";
  const disableClose = baseDisabled || status === "Closed";

  return (
    <div className="flex items-center gap-2">
      <span className={`px-2 py-1 rounded text-xs ${badgeColor}`}>
        {t("valve_status")}: {t(status.toLowerCase()) || status}
      </span>
      <button
        onClick={() => runAction("open")}
        disabled={disableOpen}
        className={`px-2 py-1 rounded text-white text-xs ${
          disableOpen ? "bg-gray-400" : "bg-emerald-600 hover:bg-emerald-700"
        }`}
      >
        {isLoading
          ? t("working") || "Working..."
          : t("open_valve") || "Open Valve"}
      </button>
      <button
        onClick={() => runAction("close")}
        disabled={disableClose}
        className={`px-2 py-1 rounded text-white text-xs ${
          disableClose ? "bg-gray-400" : "bg-red-600 hover:bg-red-700"
        }`}
      >
        {isLoading
          ? t("working") || "Working..."
          : t("close_valve") || "Close Valve"}
      </button>
      {error && <span className="text-xs text-red-700 ml-1">{error}</span>}
    </div>
  );
};

ValveControl.propTypes = {
  guid: PropTypes.string,
  pollMs: PropTypes.number,
  confirmBeforeAction: PropTypes.bool,
};

export default ValveControl;
